import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setApiToken, setTokenRefreshCallback } from './api/authApiClient';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { fetchAdminStatus, fetchEmail, fetchTenantId, fetchDistributor } from './app/userInfoSlice';

const TokenContext = createContext(false);
export const useTokenReady = () => useContext(TokenContext);

const isSessionError = (error) => {
  const code = String(error?.error || '').toLowerCase();
  const message = String(error?.message || error || '').toLowerCase();
  return ['login_required', 'consent_required', 'missing_refresh_token', 'invalid_grant'].includes(code)
    || /missing refresh token|invalid state|login required|session.*expired/.test(message);
};

const currentReturnTo = () => `${window.location.pathname}${window.location.hash || ''}`;

export const TokenReadyProvider = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    user,
    loginWithRedirect,
    error,
  } = useAuth0();
  const dispatch = useDispatch();
  const redirectStarted = useRef(false);
  const bootstrapStarted = useRef(false);
  const [ready, setReady] = useState(false);
  const [fatalError, setFatalError] = useState('');

  const startCleanLogin = useCallback(async () => {
    if (redirectStarted.current) return;
    redirectStarted.current = true;
    setReady(false);
    setFatalError('');

    // Auth0Provider owns callback processing. Remove a stale/failed callback
    // before starting one new transaction; never call handleRedirectCallback here.
    if (window.location.search.includes('code=') || window.location.search.includes('state=')) {
      window.history.replaceState({}, document.title, currentReturnTo());
    }

    try {
      await loginWithRedirect({
        appState: { returnTo: currentReturnTo() },
        authorizationParams: { prompt: 'login' },
      });
    } catch (loginError) {
      redirectStarted.current = false;
      setFatalError('Your session expired and sign-in could not be started. Reload this page to try again.');
      console.error('Failed to restart authentication:', loginError);
    }
  }, [loginWithRedirect]);

  useEffect(() => {
    const refreshToken = async () => {
      try {
        const token = await getAccessTokenSilently({ cacheMode: 'off' });
        setApiToken(token);
        return token;
      } catch (refreshError) {
        if (isSessionError(refreshError)) void startCleanLogin();
        throw refreshError;
      }
    };
    setTokenRefreshCallback(refreshToken);
  }, [getAccessTokenSilently, startCleanLogin]);

  useEffect(() => {
    if (isLoading || redirectStarted.current) return;
    if (!isAuthenticated) {
      void startCleanLogin();
      return;
    }
    if (bootstrapStarted.current || ready) return;
    bootstrapStarted.current = true;

    const bootstrap = async () => {
      try {
        // The redirect callback has already cached a valid access token. Use it
        // for initial bootstrap instead of forcing an immediate refresh, which
        // can fail in privacy/incognito contexts that block the Auth0 session.
        const token = await getAccessTokenSilently();
        setApiToken(token);
        if (user?.sub) localStorage.setItem('subId', user.sub);
        await Promise.all([
          dispatch(fetchAdminStatus()).unwrap().catch((requestError) => console.warn('fetchAdminStatus failed', requestError)),
          dispatch(fetchTenantId()).unwrap().catch((requestError) => console.warn('fetchTenantId failed', requestError)),
          dispatch(fetchEmail()).unwrap().catch((requestError) => console.warn('fetchEmail failed', requestError)),
          dispatch(fetchDistributor()).unwrap().catch((requestError) => console.warn('fetchDistributor failed', requestError)),
        ]);
        setReady(true);
      } catch (bootstrapError) {
        bootstrapStarted.current = false;
        console.error('Failed to initialize authenticated dashboard:', bootstrapError);
        if (isSessionError(bootstrapError) || isSessionError(error)) {
          void startCleanLogin();
        } else {
          setFatalError('The dashboard could not finish loading. Reload the page to try again.');
        }
      }
    };
    void bootstrap();
  }, [dispatch, error, getAccessTokenSilently, isAuthenticated, isLoading, ready, startCleanLogin, user]);

  useEffect(() => {
    if (!isAuthenticated || !ready) return undefined;
    const refreshInterval = setInterval(async () => {
      try {
        const token = await getAccessTokenSilently({ cacheMode: 'off' });
        setApiToken(token);
      } catch (refreshError) {
        console.warn('Periodic token refresh failed:', refreshError);
        if (isSessionError(refreshError)) void startCleanLogin();
      }
    }, 45 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [getAccessTokenSilently, isAuthenticated, ready, startCleanLogin]);

  if (fatalError) {
    return (
      <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">{fatalError}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Reload dashboard</Button>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Signing you in…</Typography>
      </Box>
    );
  }

  return <TokenContext.Provider value={true}>{children}</TokenContext.Provider>;
};
